/**
 * useAudioRecorder — in-browser microphone recorder that yields a Twilio-ready
 * WAV (Phone Settings voicemail-greeting recorder).
 *
 * WHY WAV: MediaRecorder produces webm/opus (Chrome/Firefox) or mp4/aac
 * (Safari) — neither of which Twilio's <Play> can serve. So on stop we decode
 * whatever the recorder produced via the Web Audio API and re-encode it to a
 * small mono 8 kHz 16-bit PCM WAV (telephony-native, a few tens of KB, and in
 * crmS3's allow-list). No server transcoding / ffmpeg dependency.
 *
 * The hook owns the mic stream, a 1 s elapsed timer, and a hard MAX_SEC
 * auto-stop. It revokes its preview object URL on reset/unmount so a re-record
 * never leaks blobs.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const GREETING_MAX_SEC = 45;
const TARGET_RATE = 8000; // telephony sample rate; Twilio resamples regardless

export type RecorderStatus = 'idle' | 'recording' | 'processing' | 'recorded' | 'error';

interface AudioRecorder {
  status: RecorderStatus;
  elapsedSec: number;
  error: string | null;
  /** Object URL for previewing the just-recorded clip (null until recorded). */
  previewUrl: string | null;
  /** The encoded WAV blob, ready to upload (null until recorded). */
  blob: Blob | null;
  /** Recorded clip length in seconds (null until recorded). */
  durationSec: number | null;
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
}

/** Downmix to mono, linearly resample to TARGET_RATE, and write a 16-bit PCM WAV. */
function encodeWav(audioBuffer: AudioBuffer): Blob {
  const numCh = audioBuffer.numberOfChannels;
  const srcRate = audioBuffer.sampleRate;
  const srcLen = audioBuffer.length;

  // Mono downmix (average channels).
  const mono = new Float32Array(srcLen);
  for (let ch = 0; ch < numCh; ch++) {
    const data = audioBuffer.getChannelData(ch);
    for (let i = 0; i < srcLen; i++) mono[i] += data[i] / numCh;
  }

  // Linear resample to TARGET_RATE (fine for speech).
  const ratio = TARGET_RATE / srcRate;
  const outLen = Math.max(1, Math.round(srcLen * ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i / ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(srcLen - 1, i0 + 1);
    const frac = pos - i0;
    out[i] = mono[i0] * (1 - frac) + mono[i1] * frac;
  }

  // 44-byte WAV header + 16-bit PCM samples.
  const bytesPerSample = 2;
  const blockAlign = bytesPerSample; // mono
  const dataSize = outLen * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM fmt chunk size
  view.setUint16(20, 1, true); // audio format = PCM
  view.setUint16(22, 1, true); // channels = mono
  view.setUint32(24, TARGET_RATE, true);
  view.setUint32(28, TARGET_RATE * blockAlign, true); // byte rate
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  let off = 44;
  for (let i = 0; i < outLen; i++) {
    const s = Math.max(-1, Math.min(1, out[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return new Blob([view], { type: 'audio/wav' });
}

export function useAudioRecorder(): AudioRecorder {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsedSec, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [durationSec, setDurationSec] = useState<number | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);
  const previewUrlRef = useRef<string | null>(null);
  // Generation guard: bumped on every start()/reset()/unmount so a take that was
  // reset or unmounted mid-record can't be resurrected by its async onstop.
  const genRef = useRef(0);

  const stopTracks = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const reset = useCallback(() => {
    genRef.current += 1; // invalidate any in-flight onstop from a prior take
    stopTracks();
    try { recorderRef.current?.state !== 'inactive' && recorderRef.current?.stop(); } catch { /* noop */ }
    recorderRef.current = null;
    chunksRef.current = [];
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
    setPreviewUrl(null);
    setBlob(null);
    setDurationSec(null);
    setElapsed(0);
    setError(null);
    setStatus('idle');
  }, [stopTracks]);

  const start = useCallback(async () => {
    setError(null);
    // Clear any prior take.
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
    setPreviewUrl(null);
    setBlob(null);
    setDurationSec(null);
    chunksRef.current = [];
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Recording is not supported in this browser.');
      setStatus('error');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('Microphone permission is required to record a greeting.');
      setStatus('error');
      return;
    }
    streamRef.current = stream;
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream);
    } catch {
      stopTracks();
      setError('Could not start recording on this device.');
      setStatus('error');
      return;
    }
    const myGen = ++genRef.current; // this take's identity; a later reset/start invalidates it
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    recorder.onstop = async () => {
      stopTracks();
      // If this take was reset/unmounted while stopping, don't resurrect it.
      if (genRef.current !== myGen) return;
      setStatus('processing');
      try {
        const recorded = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || 'audio/webm' });
        const arrayBuf = await recorded.arrayBuffer();
        const Ctx = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = new Ctx();
        // decodeAudioData promise/callback shim — older Safari/WebKit (<14.1)
        // only implements the callback form and returns undefined.
        const audioBuffer: AudioBuffer = await new Promise((resolve, reject) => {
          const p = (ctx as any).decodeAudioData(arrayBuf, resolve, reject);
          if (p && typeof p.then === 'function') p.then(resolve, reject);
        });
        const dur = audioBuffer.duration;
        const wav = encodeWav(audioBuffer);
        try { await ctx.close(); } catch { /* noop */ }
        if (genRef.current !== myGen) return; // reset/unmount happened during decode
        const objUrl = URL.createObjectURL(wav);
        previewUrlRef.current = objUrl;
        setPreviewUrl(objUrl);
        setBlob(wav);
        setDurationSec(Math.round(dur));
        setStatus('recorded');
      } catch {
        if (genRef.current !== myGen) return;
        setError('Could not process the recording. Please try again.');
        setStatus('error');
      }
    };
    startedAtRef.current = Date.now();
    setElapsed(0);
    setStatus('recording');
    recorder.start();
    timerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(secs);
      if (secs >= GREETING_MAX_SEC) {
        try { recorder.state !== 'inactive' && recorder.stop(); } catch { /* noop */ }
      }
    }, 250);
  }, [stopTracks]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // onstop normally tears down the mic via stopTracks(); if stop() throws,
      // onstop never fires, so release the mic + timer here too (no hot mic left).
      try { recorder.stop(); } catch { stopTracks(); setStatus('error'); }
    }
  }, [stopTracks]);

  // Tear down on unmount: invalidate any in-flight take, stop the mic, revoke URL.
  useEffect(() => () => {
    genRef.current += 1;
    stopTracks();
    try { recorderRef.current?.state !== 'inactive' && recorderRef.current?.stop(); } catch { /* noop */ }
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, [stopTracks]);

  return { status, elapsedSec, error, previewUrl, blob, durationSec, start, stop, reset };
}
